/* eslint-disable react/jsx-props-no-spreading */
import { FormControl, Grid, TextField, Typography } from '@mui/material';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Button } from '@efexteam/components';
import DataLoadingWrapper from '../../../../components/common/DataLoadingWrapper';
import ErrorRetry from '../../../../components/common/ErrorRetry';
import { UserType } from '../../../../configs/language';
import { IValidateContactEmailCamelCase } from '../../../../services/client/types';
import { protectString } from '../../../../utils/formater';
import { useFormData } from '../context';

interface IConfirmExistingContactInformation {
  formStep: number;
  contact: IValidateContactEmailCamelCase;
  loading: boolean;
  error: Error | undefined;
  nextFormStep: () => void;
  prevStep: () => void;
  retry: () => void;
}

type FormData = {
  entity: string;
  email: string;
  bankName: string;
  representativeName: string;
  contactPhone: string;
  companyName: string;
  country: string;
  address: string;
  cityState: string;
  accountNumber: string;
  routingNumber: string;
  clabe: string;
  nickName: string;
};

const ConfirmExistingContactInformation = ({
  formStep,
  contact,
  loading,
  error,
  nextFormStep,
  prevStep,
  retry,
}: IConfirmExistingContactInformation) => {
  const { t } = useTranslation();
  const { setFormValues } = useFormData();

  const dropdownOptionsEntity: { key: string; value: UserType }[] = [
    { key: t('isNewUserCompany'), value: 'Company' },
    { key: t('isNewUserPerson'), value: 'Person' },
  ];

  const {
    handleSubmit,
    formState: { errors },
    setValue,
    register,
  } = useForm<FormData>({ mode: 'all' });

  const onSubmit = handleSubmit((values) => {
    setFormValues(values);
    nextFormStep();
  });

  const handleBack = () => {
    prevStep();
  };

  const contactCompany = dropdownOptionsEntity.find(
    (opt) =>
      opt.value.toLowerCase() === contact.enterprise.entityType.toLowerCase()
  )?.key;

  useEffect(() => {
    if (contact) {
      const cityStateFixed = `${contact.addresses[0]?.city || ''} - ${
        contact.addresses[0]?.state || ''
      }`;
      setValue('entity', t(contactCompany || 'ccEntityPerson'));
      setValue('email', contact.naturalPerson.email || '');
      setValue('bankName', contact.accounts[0]?.bankName || '');
      setValue(
        'representativeName',
        `${contact.naturalPerson.firstName || ''} ${
          contact.naturalPerson.lastName || ''
        }`
      );
      setValue('contactPhone', contact.naturalPerson.phoneNumber || '');
      setValue('companyName', contact.enterprise.name || '');
      setValue('country', contact.naturalPerson.country || '');
      setValue('address', contact.addresses[0]?.addressLine1 || '');
      setValue('cityState', cityStateFixed);
      setValue(
        'accountNumber',
        protectString(contact.accounts[0]?.accountNumber || '')
      );
      setValue(
        'routingNumber',
        protectString(contact.accounts[0]?.routingNumber || '')
      );
      setValue('clabe', protectString(contact.accounts[0]?.clabe || ''));
      setValue('nickName', contact.naturalPerson.nickName || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contact]);

  return (
    <>
      <Typography variant="h6" className="!my-4 text-center">
        {t('ccProviderTitle')}
      </Typography>

      <form onSubmit={onSubmit} autoComplete="off">
        <Grid container rowSpacing={2} columnSpacing={{ xs: 2, sm: 2, md: 3 }}>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <TextField
                disabled
                id="entity"
                label={t('entity')}
                error={!!errors.entity}
                {...register('entity')}
              />
              {errors.entity && (
                <p className="text-orange-600">{t('defaultError')}</p>
              )}
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <TextField
                disabled
                id="representativeName"
                label={t('ccRepresentativeName')}
                error={!!errors.representativeName}
                {...register('representativeName')}
              />
              {errors.representativeName && (
                <p className="text-orange-600">{t('requiredField')}</p>
              )}
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <TextField
                disabled
                id="companyName"
                label={t('companyName')}
                error={!!errors.companyName}
                value={contact.enterprise.name || ''}
                {...register('companyName')}
              />
              {errors.companyName && (
                <p className="text-orange-600">{t('requiredField')}</p>
              )}
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <TextField
                disabled
                id="nickName"
                label={t('nickName')}
                error={!!errors.nickName}
                {...register('nickName')}
              />
              {errors.nickName && (
                <p className="text-orange-600">{t('requiredField')}</p>
              )}
            </FormControl>
          </Grid>
        </Grid>

        <Grid
          item
          xs={12}
          className="flex justify-between !flex-col md:!flex-row"
        >
          {formStep > 0 && (
            <Button
              appearance="secondary"
              className="mt-4"
              size="large"
              onClick={handleBack}
              type="button"
            >
              {t('backBtn')}
            </Button>
          )}
          {!error ? (
            <DataLoadingWrapper loadingData={loading} variant="spinner">
              <Button
                appearance="primary"
                size="large"
                className="mt-4"
                type="submit"
              >
                {t('ccAddContact')}
              </Button>
            </DataLoadingWrapper>
          ) : (
            <ErrorRetry
              showImage={false}
              buttonClassNames="min-w-[200px]"
              onRetry={retry}
            />
          )}
        </Grid>
      </form>
    </>
  );
};

export default ConfirmExistingContactInformation;
